import { Flex, Text, useBreakpointValue, useToast } from '@chakra-ui/react';
import { JustSkeleton, UpsellCarousel, UpsellProductModal } from '@getjust/leaf';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { UpsellProduct } from '@getjust/gateway';

import { useThemeContext } from '$src/context/ThemeContext';
import { useLockBackgroundScroll } from '$src/hooks/client/useLockBackgroundScroll';
import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { useAddLineItems } from '$src/hooks/mutations/useAddLineItems';
import { useCart } from '$src/hooks/queries/useCart';
import { useUpsellProducts } from '$src/hooks/queries/useUpsellProducts';
import { useCheckoutFeaturesAtom } from '$src/hooks/state/useCheckoutFeaturesAtom';
import { useEmailAtom } from '$src/hooks/state/useEmailAtom';

import { Check } from '../SvgLogos/Check';

const TOAST_BG_COLORS = {
  soft: '#000000',
  classic: '#000000',
  custom: '#000000',
};

const TOAST_TEXT_COLORS = {
  soft: 'var(--tooltip-text)',
  classic: 'var(--tooltip-text)',
  custom: 'var(--tooltip-text)',
};

export const Upsell = ({
  requireEmail = true,
  cardWidth,
  maxProducts,
  pager,
  layout,
}: {
  requireEmail?: boolean;
  cardWidth?: string;
  maxProducts?: number;
  pager?: 'arrows' | 'dots';
  layout?: 'scroll' | 'grid';
} = {}) => {
  const toast = useToast();
  const { t } = useTranslation();
  // On mobile the bottom of the viewport is occupied by the sticky cart recap +
  // pay button, which cover a `position: 'bottom'` toast. Drop the "produit
  // ajouté" notification from the top on mobile (desktop keeps the bottom).
  const isMobile = useBreakpointValue({ base: true, md: false });
  const toastPosition = isMobile ? 'top' : 'bottom';
  const toastMargin = isMobile
    ? { mt: 'max(12px, env(safe-area-inset-top))' }
    : { mb: 'max(12px, env(safe-area-inset-bottom))' };
  const { data: cart } = useCart();
  const { email } = useEmailAtom();
  const { track } = useTrackAnalytics();
  const { mutateAsync: addLineItems } = useAddLineItems();
  const { canUseCheckoutFeature } = useCheckoutFeaturesAtom();
  const lastTrackedProductsLength = useRef<number | undefined>(undefined);
  const { themeName } = useThemeContext();

  const { data: upsellData, isLoading: isUpsellLoading } = useUpsellProducts();
  const { upsellProducts = [], smartUpsellAbTestVariant } = upsellData ?? {};

  const variantIdsInCart = useMemo(() => cart?.lineItems?.map((item) => item.variantId) ?? [], [cart]);
  const showQuantityAvailable = canUseCheckoutFeature('product_stock_indicator') || false;

  // Modal state
  const [selectedProduct, setSelectedProduct] = useState<UpsellProduct | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Lock the background checkout scroll while the product sheet/modal is open.
  useLockBackgroundScroll(isModalOpen);
  // Variants optimistically removed from the suggestions the instant "Add to
  // cart" is clicked (rolled back if the add fails).
  const [optimisticVariantIds, setOptimisticVariantIds] = useState<string[]>([]);

  // The carousel hides real in-cart variants plus the optimistically-added ones,
  // so a product leaves the suggestions on click rather than after the round-trip.
  const carouselVariantIdsInCart = useMemo(
    () => Array.from(new Set([...variantIdsInCart, ...optimisticVariantIds])),
    [variantIdsInCart, optimisticVariantIds],
  );

  const toastBg = useMemo(
    () =>
      TOAST_BG_COLORS[themeName as keyof typeof TOAST_BG_COLORS] ??
      'linear-gradient(90deg, #8DE690 2.08%, #A5F0A3 18.23%, #A6ECD7 33.33%, #97DADA 51.56%, #B3DAE7 67.71%, #B6C9EC 83.33%, #879ACB 100%)',
    [themeName],
  );

  const toastTextColor = useMemo(() => {
    return TOAST_TEXT_COLORS[themeName as keyof typeof TOAST_TEXT_COLORS] ?? 'var(--badge-text)';
  }, [themeName]);

  const handleDisplay = useCallback(
    (products: UpsellProduct[]) => {
      if (lastTrackedProductsLength.current === products.length) return;
      track(AnalyticsEventName.UPSELL_CAROUSEL_DISPLAYED, {
        classicItemsCount: products.filter((product) => product.source === 'classic').length,
        smartItemsCount: products.filter((product) => product.source === 'smart').length,
        smartUpsellAbTestVariant: String(smartUpsellAbTestVariant),
      });
      lastTrackedProductsLength.current = products.length;
    },
    [smartUpsellAbTestVariant, track],
  );

  const handleOpenProduct = useCallback(
    (product: UpsellProduct) => {
      setSelectedProduct(product);
      setIsModalOpen(true);
      track(AnalyticsEventName.UPSELL_PRODUCT_MODAL_OPENED, {
        productId: product.id,
        source: String(product.source),
        smartUpsellAbTestVariant: String(smartUpsellAbTestVariant),
      });
    },
    [smartUpsellAbTestVariant, track],
  );

  const handleCloseModal = useCallback(
    (didAdd = false) => {
      setIsModalOpen(false);
      track(AnalyticsEventName.UPSELL_PRODUCT_MODAL_CLOSED, {
        productId: selectedProduct?.id,
        didAdd,
      });
    },
    [selectedProduct?.id, track],
  );

  // Shared add path: optimistic removal from the suggestions, background
  // mutation, success/error toast, and analytics tagged with the surface the
  // add originated from ('modal' = variant picker, 'card' = grid "+" FAB).
  const addVariantToCart = useCallback(
    async (
      product: UpsellProduct,
      variant: UpsellProduct['variants'][number],
      surface: 'modal' | 'card',
    ) => {
      // Optimistic UX: drop the product from the suggestions the instant the
      // user clicks — the add runs in the background.
      setOptimisticVariantIds((prev) => (prev.includes(variant.id) ? prev : [...prev, variant.id]));
      try {
        await addLineItems({
          lineItems: [{ quantity: 1, productId: product.id, variantId: variant.id }],
        });
        track(AnalyticsEventName.PRODUCT_ADDED_TO_CART_VIA_UPSELL, {
          productId: product.id,
          variantId: variant.id,
          upsellProductSource: String(product.source),
          smartUpsellAbTestVariant: String(upsellData?.smartUpsellAbTestVariant),
          surface,
        });
        toast({
          position: toastPosition,
          duration: 2200,
          render: () => (
            <Flex
              {...toastMargin}
              fontSize="14px"
              fontWeight={500}
              borderRadius="full"
              minH="44px"
              px="18px"
              py="10px"
              gap={2}
              w="fit-content"
              maxW="92vw"
              m="auto"
              boxShadow="0 8px 24px rgba(0, 0, 0, 0.18)"
              justifyContent="center"
              alignItems="center"
              bg={toastBg}
            >
              <Check strokeColor={toastTextColor} />
              <Text color={toastTextColor}>{t('upsell.productAdded')}</Text>
            </Flex>
          ),
        });
      } catch (err) {
        // Roll back the optimistic removal so the product returns to the
        // suggestions, and surface the error as a toast (the modal is closed).
        setOptimisticVariantIds((prev) => prev.filter((id) => id !== variant.id));
        console.error('[upsell] add to cart failed', err);
        toast({
          position: toastPosition,
          duration: 3000,
          render: () => (
            <Flex
              {...toastMargin}
              fontSize="14px"
              fontWeight={500}
              borderRadius="full"
              minH="44px"
              px="18px"
              py="10px"
              w="fit-content"
              maxW="92vw"
              m="auto"
              boxShadow="0 8px 24px rgba(0, 0, 0, 0.18)"
              justifyContent="center"
              alignItems="center"
              bg="var(--danger)"
            >
              <Text color="white">{t('upsell.addError')}</Text>
            </Flex>
          ),
        });
      }
    },
    [
      addLineItems,
      t,
      toast,
      toastBg,
      toastTextColor,
      toastPosition,
      toastMargin,
      track,
      upsellData?.smartUpsellAbTestVariant,
    ],
  );

  const handleModalAddToCart = useCallback(
    async (product: UpsellProduct, variant: UpsellProduct['variants'][number]) => {
      // Close the modal instantly; the add runs in the background.
      handleCloseModal(true);
      await addVariantToCart(product, variant, 'modal');
    },
    [addVariantToCart, handleCloseModal],
  );

  // Grid "+" FAB: always open the focused product modal — even for
  // single-variant products — so every add goes through the same confirmation
  // surface rather than dropping straight into the cart.
  const handleQuickAdd = useCallback(
    (product: UpsellProduct) => {
      handleOpenProduct(product);
    },
    [handleOpenProduct],
  );

  const handleVariantSelected = useCallback(
    (product: UpsellProduct, variant: UpsellProduct['variants'][number]) => {
      track(AnalyticsEventName.UPSELL_MODAL_VARIANT_SELECTED, {
        productId: product.id,
        variantId: variant.id,
      });
    },
    [track],
  );

  if (isUpsellLoading) {
    return <JustSkeleton w="full" h="100px" mt="4" loadingColor="var(--border, #E0E0E0)" />;
  }

  // No products (none configured, or the recommendation call failed) → render
  // nothing rather than an empty container that reads as dead blank space.
  if (!upsellProducts?.length) {
    return null;
  }

  return (
    <>
      <UpsellCarousel
        products={upsellProducts ?? []}
        cardWidth={cardWidth}
        maxProducts={maxProducts}
        pager={pager}
        layout={layout}
        disabled={requireEmail ? !(email || '').length : false}
        variantIdsInCart={carouselVariantIdsInCart}
        labels={{
          title: t('upsell.title'),
          nextProduct: t('upsell.nextProduct'),
          previousProduct: t('upsell.previousProduct'),
          fromPrice: t('upsell.fromPrice'),
          addToCart: t('upsell.addToCart'),
        }}
        onDisplay={handleDisplay}
        onOpenProduct={handleOpenProduct}
        onQuickAdd={handleQuickAdd}
      />
      <UpsellProductModal
        isOpen={isModalOpen}
        product={selectedProduct}
        variantIdsInCart={variantIdsInCart}
        showQuantityAvailable={showQuantityAvailable}
        isAdding={false}
        justAdded={false}
        labels={{
          addToCart: t('upsell.addToCart'),
          recommendedBadge: t('upsell.recommendedBadge'),
          added: t('upsell.added'),
          soldOut: t('upsell.soldOut'),
          inCart: t('upsell.inCart'),
          selectVariant: t('upsell.selectVariant'),
          description: t('upsell.description'),
          addError: t('upsell.addError'),
          // Raw strings with the {{count}} placeholder intact — the modal does
          // its own count replacement (so the singular/plural split stays in the
          // modal, not i18next pluralization). Shared with the cart line-item
          // low-stock indicator via commons.leftQuantity.
          leftQuantity: {
            singular: t('commons.leftQuantity'),
            plural: t('commons.leftQuantity_plurials'),
          },
        }}
        onClose={handleCloseModal}
        onAddToCart={handleModalAddToCart}
        onVariantSelected={handleVariantSelected}
      />
    </>
  );
};
